import { Modal, Button, Spinner} from 'react-bootstrap';
import { FiAlertTriangle, FiTrash2, FiX } from 'react-icons/fi';
import { useMutation } from 'react-query';
import { IUser } from '../../models/user';
import { useAuth } from '../../context/auth';

type Props = {
    user: IUser;
    cancel: React.Dispatch<React.SetStateAction<boolean>>
    setError: React.Dispatch<React.SetStateAction<string>>
}

const ConfirmDeleteAccount = ({user, cancel, setError} : Props) => {
    const handleClose = () => cancel(false);

    const {deleteUser} = useAuth();
    const { mutateAsync: deleteAccountFn, isLoading} = useMutation( deleteUser,
        {
            onSuccess: () => {
                handleClose();
            },
            onError: (error: any) => {
                if(error?.response?.data?.name === "OrganizationOwnershipError") {
                    setError("You cannot delete your account because you are the owner of an organization. Please delete or transfer ownership to another member first.");
                } else {
                    setError("Unexpected error. Please try again or contact admin.");
                }
                handleClose();
                return;
            },
        }
    );

    const handleDelete = async () => {
        if(!user) {
            setError("User not found.");
            handleClose();
            return;
        }
        deleteAccountFn();
    }

    return (
        <Modal show={!!user} onHide={handleClose} size='lg' centered>
            <Modal.Header 
                closeButton 
                className="bg-dark border-danger"
            >
                <Modal.Title className="text-danger d-flex align-items-center">
                    <FiAlertTriangle className="me-2" size={20} />
                    Delete Account
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="bg-dark text-light">
                <div className="text-center mb-4">
                    <div className="danger-icon mb-3">
                        <FiAlertTriangle size={48} className="text-danger" />
                    </div>
                    
                    <h5 className="text-light mb-3">
                        Are you absolutely sure?
                    </h5>
                    
                    <p className="mb-4" style={{ color: '#ccc' }}>
                        You are about to permanently delete your account.
                    </p>
                </div>

                <div className="warning-box p-3 mb-4 border border-danger rounded">
                    <div className="d-flex align-items-start">
                        <FiAlertTriangle className="text-danger me-3 mt-1" size={20} />
                        <div>
                            <h6 className="text-danger mb-2">This action cannot be undone</h6>
                            <ul className=" mb-0 small" style={{ color: '#ccc' }}>
                                <li>All organizations you own will be permanently deleted</li>
                                <li>All uploaded data and knowledge base content will be lost</li>
                                <li>All trained agents will be permanently removed</li>
                                <li>Your account access will be revoked immediately</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            
            <Modal.Footer className="bg-dark border-danger">
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose}
                    disabled={isLoading}
                    className="d-flex align-items-center gap-2"
                >
                    <FiX size={16} />
                    Cancel
                </Button>
                <Button 
                    variant="danger" 
                    onClick={handleDelete}
                    disabled={isLoading}
                    className="d-flex align-items-center gap-2"
                >
                    {isLoading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            Deleting...
                        </>
                    ) : (
                        <>
                            <FiTrash2 size={16} />
                            Delete Account
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmDeleteAccount